import { apiClient } from '../../lib/apiClient';
import {
  clearStoredDevice,
  currentSubscription,
  deviceLabel,
  readStoredDevice,
  subscribe,
  subscriptionKeys,
  subscriptionMatchesKey,
  unsubscribe,
  writeStoredDevice,
} from '../../lib/push';
import type {
  PushConfigRead,
  PushKind,
  PushSubscriptionCreate,
  PushSubscriptionRead,
  PushSubscriptionUpdate,
  PushTestQueued,
} from '../../types/api';

/**
 * Push notification devices (PUSH_NOTIFICATIONS_PLAN.md §4). The backend
 * stores one row per browser; the worker process does the sending.
 */

const DEVICES = '/api/v1/me/push-subscriptions';

/** The seven kinds, in the order the Settings card lists them. */
export const PUSH_KINDS: readonly PushKind[] = [
  'review_ready',
  'slides_ready',
  'run_failed',
  'plan_needs_review',
  'plan_failed',
  'published',
  'publish_failed',
];

/** Whether this server sends push, and the key browsers subscribe with. */
export function fetchPushConfig(signal?: AbortSignal): Promise<PushConfigRead> {
  return apiClient.get<PushConfigRead>('/api/v1/push/config', { signal });
}

/** The caller's devices. No endpoints or keys come back. */
export function listPushSubscriptions(signal?: AbortSignal): Promise<PushSubscriptionRead[]> {
  return apiClient.get<PushSubscriptionRead[]>(DEVICES, { signal });
}

/**
 * 201 with the row. 422 `PUSH_ENDPOINT_INVALID` for an endpoint that is not a
 * known push service, 503 `INTEGRATION_DISABLED` when push is off.
 */
export function createPushSubscription(
  body: PushSubscriptionCreate,
): Promise<PushSubscriptionRead> {
  return apiClient.post<PushSubscriptionRead>(DEVICES, { body });
}

/** 404 `RESOURCE_NOT_FOUND` when the row is gone. */
export function updatePushSubscription(
  id: string,
  body: PushSubscriptionUpdate,
): Promise<PushSubscriptionRead> {
  return apiClient.patch<PushSubscriptionRead>(`${DEVICES}/${id}`, { body });
}

/** 204, idempotent. */
export function deletePushSubscription(id: string): Promise<void> {
  return apiClient.delete<void>(`${DEVICES}/${id}`);
}

/** 202. A second click inside the same minute sends one notification. */
export function sendTestPush(id: string): Promise<PushTestQueued> {
  return apiClient.post<PushTestQueued>(`${DEVICES}/${id}/test`);
}

/**
 * Subscribes this browser (asking for permission if it has not been given)
 * and registers it for `userId`. Reuses the browser's subscription when it was
 * made with the same server key, else replaces it. `kinds` carries the
 * choices over when an old row is being replaced.
 */
export async function registerThisDevice(
  userId: string,
  publicKey: string,
  kinds?: PushKind[],
): Promise<PushSubscriptionRead> {
  let subscription = await currentSubscription();
  if (subscription && !subscriptionMatchesKey(subscription, publicKey)) {
    await subscription.unsubscribe().catch(() => false);
    subscription = null;
  }
  subscription ??= await subscribe(publicKey);

  const fields = subscriptionKeys(subscription);
  if (!fields) throw new Error('This browser did not give a usable subscription.');

  const previous = readStoredDevice(userId);
  const body: PushSubscriptionCreate = { ...fields, label: deviceLabel() };
  if (kinds) body.kinds = kinds;
  const row = await createPushSubscription(body);
  writeStoredDevice(userId, { id: row.id, endpoint: fields.endpoint });

  // A replaced subscription leaves its old row behind; the registration
  // function only clears rows with the same endpoint.
  if (previous && previous.id !== row.id && previous.endpoint !== fields.endpoint) {
    await deletePushSubscription(previous.id).catch(() => undefined);
  }
  return row;
}

/** Deletes this browser's row and drops its subscription. */
export async function forgetThisDevice(userId: string): Promise<void> {
  const stored = readStoredDevice(userId);
  if (stored) await deletePushSubscription(stored.id);
  clearStoredDevice(userId);
  await unsubscribe();
}

/** Sign-out never waits longer than this for the cleanup. */
const SIGN_OUT_CLEANUP_MS = 3000;

/**
 * Sign-out cleanup (ruling 12): the next person on a shared browser must not
 * get this user's notices. Best effort and bounded: it never throws and never
 * holds sign-out up for more than a few seconds. If the server call fails, the
 * browser's subscription is still dropped, and the push service's 404 or 410
 * removes the row on the next send.
 */
export async function releasePushOnSignOut(userId: string): Promise<void> {
  const cleanup = (async () => {
    const stored = readStoredDevice(userId);
    clearStoredDevice(userId);
    if (stored) {
      const subscription = await currentSubscription().catch(() => null);
      // Only this browser's own row: another device's stays on.
      if (!subscription || subscription.endpoint === stored.endpoint) {
        await deletePushSubscription(stored.id).catch(() => undefined);
      }
    }
    await unsubscribe();
  })().catch(() => undefined);

  await Promise.race([
    cleanup,
    new Promise<void>((resolve) => window.setTimeout(resolve, SIGN_OUT_CLEANUP_MS)),
  ]);
}
